/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

import isNil from 'lodash/isNil';

import DhtmlxRecFrequency from '../dhtmlx-rec-frequency';
import IcalFreq from '../../ical/ical-freq';

// TODO Lepší parsování `rec_type` formátu:
// - Vyčlenit do samostatné třídy, nevhodné výpočty v konstruktoru.
// - Možnost lazy-parsování, až když je hodnota požadována.
// - Možnost použití regex místo 2x split.
//   Aktuální implementace je kompromisem mezi složitostí implementace
//   a dodržením ne úplně jasně specifikovaného DHTMLX rec_type formátu.
//   Takto hodnotu parsuje vzorový PHP.
// - Kontrolovat validitu naparsovaných hodnot (NaN, null, whitespace).
// TODO Opravdu podporovat i položky days bez hodnot?
// - Nedává moc smysl, ale ve výstupu z PHP předvodu (z iCalendar do DHTMLX)
//   se občas objevuje včetně znaků jako `\r` apod.
// TODO Lepší přístup k parsováním hodnot dle typu / frekvence opakování.
// - Formát DHTMLX obecně vychází z typu (day, week, ...) a dle toho se použijí
//   vybrané hodnoty a ostatní se ignorují.
//   Možná by bylo lepší postavit parsování na těchto typech a pro každý typ
//   vracet odlišný ICAL.Recur objekt pouze s hodnotami, které jsou pro daný
//   typ platné.
// - Nyní se parsuje "vše v jednom" a výstup se určuje spíše podle přítomnosti
//   jednotlivých parametrů.
// TODO Bug v ical.js s DTSTART a BY*
// - https://github.com/mozilla-comm/ical.js/issues/434
// - Projevuje se např. u test18:
//   Pokud se nepřidá BYMONTH, tak se výpočty neprovádějí korektně.
//   První výskyt se udává: 20210108T000000
// - Při výpočtu "z hlavy" a také v Thunderbirdu by ale počáteční datum
//   mělo být 20200814T000000.
// - Po explicitním přidání BYMONTH je již vše v pořádku.
// - Obecně jde o to, že při ročním opakování bez BYMONTH by se měla tato
//   hodnota převzít z měsíce u DTSTART, ale místo toho se to interpretuje
//   jako leden (1. měsíc).
// - Jako hotfix jsou zatím přidány explicitní definice BYMONTH
//   a BYMONTHDAY, i když by tam vůbec nemusely být.
//   Díky tomu je výstup zbytečně veliký.
//   Také se provádějí zbytečně složité výpočty, hlavně v getByMonthDay.
/**
 * DHTMLX Scheduler rec_type s informacemi v iCalendar formátu
 *
 * Zdroje:
 * - [DHTMLX Recurring Events](https://docs.dhtmlx.com/scheduler/recurring_events.html)
 * - [iCalendar Recurrence Rule](https://tools.ietf.org/html/rfc5545#section-3.3.10)
 */
class DhtmlxToIcalRecType {
  // TODO Bug v eslint
  // eslint-disable-next-line jsdoc/require-returns
  /**
   * @param {IcalApi} ICAL
   * @param {DhtmlxEvent} event
   * @param {IcalUtil} icalUtil
   * @param {DhtmlxRecTypeUtil} recTypeUtil
   */
  constructor(ICAL, event, icalUtil, recTypeUtil) {
    this.ICAL = ICAL;
    this.event = event;
    this.recTypeOptions = recTypeUtil.parse(event.getRecType());
    this.icalUtil = icalUtil;
  }

  /**
   * Zjistí část BYDAY z iCalendar RRULE.
   * Jde o značně omezenou funkcionalitu, než podporuje iCalendar.
   * U týdenního plánováni je definován seznam dnů, ale bez "count2".
   * U měsíčního a ročního plánování je zde jen jeden den a "count2".
   * Také nepodporuje záporné hodnoty jako iCalendar.
   * @returns {Array.<string>}
   */
  getByDay() {
    const { count2, day, days } = this.recTypeOptions;
    // TODO Nemělo by se rozhodovat dle typu opakování?
    if (isNil(day) || isNil(count2)) {
      return days.map((d) => this.icalUtil.parseRecurDay(d));
    }
    return [`${count2}${this.icalUtil.parseRecurDay(day)}`];
  }

  /**
   * Zjistí část BYMONTH z iCalendar RRULE.
   * @returns {Array.<number>}
   */
  getByMonth() {
    if (this.recTypeOptions.type !== DhtmlxRecFrequency.Year) {
      return [];
    }
    return [this.event.getStartDate().month];
  }

  /**
   * Zjistí část BYMONTHDAY z iCalendar RRULE.
   * @returns {Array.<number>}
   */
  getByMonthDay() {
    const { type } = this.recTypeOptions;
    if (type !== DhtmlxRecFrequency.Month && type !== DhtmlxRecFrequency.Year) {
      return [];
    }
    if (this.getByDay().length) {
      return [];
    }
    return [this.event.getStartDate().day];
  }

  /**
   * Zjistí část COUNT z iCalendar RRULE.
   * @returns {number}
   */
  getCount() {
    return this.recTypeOptions.count3;
  }

  /**
   * Zjistí část FREQ z iCalendar RRULE.
   * @returns {IcalFreq}
   */
  getFreq() {
    switch (this.recTypeOptions.type) {
      case DhtmlxRecFrequency.Day:
        return IcalFreq.Daily;
      case DhtmlxRecFrequency.Week:
        return IcalFreq.Weekly;
      case DhtmlxRecFrequency.Month:
        return IcalFreq.Monthly;
      case DhtmlxRecFrequency.Year:
        return IcalFreq.Yearly;
      default:
        throw new RangeError('Unknown type');
    }
  }

  /**
   * Zjistí část INTERVAL z iCalendar RRULE.
   * @returns {number}
   */
  getInterval() {
    return this.recTypeOptions.count;
  }

  /**
   * Zjistí celé iCalendar RRULE.
   * @returns {IcalApi.Recur}
   */
  getRecur() {
    const opts = {
      count: this.getCount(),
      freq: this.getFreq(),
      interval: this.getInterval(),
      until: this.getUntil()
    };
    // iCal API nedokáže ignorovat prázdné pole v byday.
    const byDay = this.getByDay();
    if (byDay.length) {
      opts.byday = byDay;
    }
    // iCal API nedokáže ignorovat prázdné pole v bymonthday.
    const byMonthDay = this.getByMonthDay();
    if (byMonthDay.length) {
      opts.bymonthday = byMonthDay;
    }
    // iCal API nedokáže ignorovat prázdné pole v bymonth.
    const byMonth = this.getByMonth();
    if (byMonth.length) {
      opts.bymonth = byMonth;
    }
    return new this.ICAL.Recur(opts);
  }

  /**
   * Zjistí část UNTIL z iCalendar RRULE.
   * @returns {IcalApi.Time}
   */
  getUntil() {
    const { recTypeOptions } = this;
    // Pokud je definován počet opakování, nebo je počet neomezený,
    // tak se UNTIL nesmí vypsat, jelikož by nebylo jasné, která z hodnot platí.
    if (recTypeOptions.infinite || !isNil(recTypeOptions.count3)) {
      return;
    }
    // Bez počtu opakování se použije hodnota v "end_date".
    return this.event.getEndDate();
  }
}

export default DhtmlxToIcalRecType;
